import { sessionsLeftView, type SessionsLeft } from "@teamna/shared";
import { Badge } from "./Badge.tsx";

/**
 * The sessions-left bar on the attendance register (signed off 2026-10-29, prototype
 * `ai-personal-assistant/teamna/prototypes/attendance-sessions-left.html`). One dash for each session
 * of the package: grey used, blue today, striped blue today with no booking yet (Came takes it),
 * empty left. Amber at 2 left or fewer. Red only for no sessions left: a red tag and a red striped
 * dash after the bar. Paused and Not paid are grey tags. More than 16 sessions: one plain bar.
 *
 * The words wrap under the dashes when the column is narrow, so the bar never runs under Yes and No.
 */
export function SessionsLeftBar({ facts, size = "row" }: { facts: SessionsLeft; size?: "row" | "card" | "mini" }) {
  const view = sessionsLeftView(facts);
  const width = size === "card" ? 170 : size === "mini" ? 70 : 90;
  const words = size === "card" ? view.longWords : view.words;
  const card = size === "card";

  if (view.tone === "none" || (!view.dashes && view.usedShare === null)) {
    return (
      <span className={`flex flex-wrap items-center gap-1.5 ${card ? "justify-center" : ""}`}>
        <Tag tone="grey">{words}</Tag>
        {view.tags.map((tag) => (
          <Tag key={tag} tone="grey">
            {tag}
          </Tag>
        ))}
      </span>
    );
  }

  const label =
    view.tone === "zero" ? (
      <Tag tone="red">{words}</Tag>
    ) : view.tone === "last" ? (
      <Tag tone="amber">{words}</Tag>
    ) : (
      <span className={`whitespace-nowrap text-[12px] font-semibold tabular-nums ${view.tone === "few" ? "text-late" : "text-ink-2"}`}>{words}</span>
    );

  return (
    <span
      className={`flex max-w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-1 ${card ? "flex-col justify-center" : ""}`}
      aria-label={[words, ...view.tags].join(". ")}
    >
      <span className="flex min-w-0 items-center" style={{ width: `min(${width}px, 100%)` }} aria-hidden="true">
        {view.dashes ? (
          <span className="flex min-w-0 flex-1 gap-[2px]">
            {view.dashes.map((dash, index) => (
              <span key={index} className={`h-1.5 min-w-0 flex-1 rounded-[2px] ${dashClass(dash, view.tone === "few")}`} />
            ))}
          </span>
        ) : (
          <span className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-sunken shadow-[inset_0_0_0_1px_var(--color-line-strong)]">
            <span className="absolute inset-y-0 left-0 bg-ink-3" style={{ width: `${(view.usedShare ?? 0) * 100}%` }} />
            {facts.today === "taken" ? <span className="absolute inset-y-0 w-[3px] bg-accent" style={{ left: `${(view.usedShare ?? 0) * 100}%` }} /> : null}
          </span>
        )}
        {view.over ? <span className="ml-[3px] h-1.5 w-3.5 shrink-0 rounded-[2px] att-dash-over" /> : null}
      </span>
      {label}
      {view.tags.map((tag) => (
        <Tag key={tag} tone="grey">
          {tag}
        </Tag>
      ))}
    </span>
  );
}

function dashClass(dash: "used" | "today" | "open" | "left", few: boolean): string {
  if (dash === "used") return "bg-ink-3";
  if (dash === "today") return "bg-accent";
  if (dash === "open") return "att-dash-open";
  return few ? "bg-late-surface shadow-[inset_0_0_0_1px_var(--color-late-line)]" : "bg-sunken shadow-[inset_0_0_0_1px_var(--color-line-strong)]";
}

/** The shared raised badge (`Badge`, `.sk-badge`): red is cancelled, amber is late, grey is quiet. */
function Tag({ tone, children }: { tone: "red" | "amber" | "grey"; children: string }) {
  return <Badge tone={tone === "red" ? "cancelled" : tone === "amber" ? "late" : "quiet"}>{children}</Badge>;
}
